// import components
import UnsavedDataAlert from 'components/shared/CRUD/components/InnerPage/components/UnsavedDataAlert';
import EditFooter from './components/EditFooter';
import ToolbarContent from './components/ToolbarContent';

// import hooks
import { FormProvider } from 'react-hook-form';
import usePreviousLocation from './hooks/usePreviousLocation';

// import react hooks, components, and types
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { StyledPaper } from 'components/shared/Paper/StyledPaper.styles';

// import styles
import { StyledUnsavedDataAlertBox } from './EditPage.styles';

// import types
import type { EditPageProps } from './EditPage.types';

// import context
import { EditPageContext } from './EditPageContext';

// imports external libraries
import { AnimatePresence } from 'motion/react';

const EditPage: React.FC<PropsWithChildren<EditPageProps>> = ({
  children,
  withTabs = false,
  methods,
  onSubmit,
  toggleInnerLayout,
  onCancel,
  setCurrentView,
  views,
  currentView,
  isEditable,
  navigatorValueGetter,
  navigatorLabelGetter,
  onSetEditMode,
  onIsDirty
}) => {
  const [isInEditMode, setIsInEditMode] = useState(false);
  const { formState } = methods;

  const handleSetEditMode = (editMode: boolean) => {
    onSetEditMode && onSetEditMode(editMode);
    setIsInEditMode(editMode);
  };

  const handleCancel = () => {
    onCancel && onCancel();
    methods.reset(); // Reset form state
    handleSetEditMode(false);
  };

  useEffect(() => {
    onIsDirty && onIsDirty(formState.isDirty);
  }, [formState.isDirty]);

  useEffect(() => {
    isEditable && handleSetEditMode(!isInEditMode);
  }, [isEditable]);

  usePreviousLocation(toggleInnerLayout);

  return (
    <StyledPaper>
      <AnimatePresence mode="wait">
        {formState.isDirty && !withTabs && (
          <StyledUnsavedDataAlertBox
            key="motion-unsaved-edit-page"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <UnsavedDataAlert />
          </StyledUnsavedDataAlertBox>
        )}
      </AnimatePresence>
      <EditPageContext.Provider
        value={{
          isEditable: isInEditMode
        }}
      >
        <FormProvider {...methods}>
          <ToolbarContent
            setIsInEditMode={handleSetEditMode}
            isInEditMode={isInEditMode}
            toggleInnerLayout={toggleInnerLayout}
            setCurrentView={setCurrentView}
            views={views}
            currentView={currentView}
            navigatorValueGetter={navigatorValueGetter}
            navigatorLabelGetter={navigatorLabelGetter}
          />
          <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            {children}
            {isInEditMode && (
              <EditFooter
                onCancelClick={handleCancel}
                submitDisabled={!formState.isDirty}
              />
            )}
          </form>
        </FormProvider>
      </EditPageContext.Provider>
    </StyledPaper>
  );
};

export default EditPage;
